import { LoginDto, RegisterDto, ResetPasswordDto } from '../infrastructure/dto/AuthDto';

import { AuthUseCase } from '../domain/usecases/AuthUseCase';
import { LoginValues } from '../infrastructure/StorageService';
import { UserCredentialsService } from './UserCredentialsService';
import { DASHBOARD_PATH } from '@/common/main/config';
import { EUM_EMBED_USERS_PATH, readEmbeddedModeFromStorage } from '@/common/application/embeddedMode';

/**
 * Application service that handles authentication requests
 * Coordinates between domain layer and UI
 */
export class AuthService {
  constructor(
    private readonly authUseCase: AuthUseCase,
    private readonly userCredentialsService: UserCredentialsService
  ) {}

  /**
   * Save the current path as returnTo URL
   */
  saveReturnToUrl(): void {
    // Check if we're in a browser environment before accessing window
    if (typeof window === 'undefined') {
      return;
    }

    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('returnTo', currentPath);
  }

  /**
   * Get and clear the returnTo URL
   */
  getAndClearReturnToUrl(): string | null {
    const returnTo = sessionStorage.getItem('returnTo');
    sessionStorage.removeItem('returnTo');
    return returnTo;
  }

  /**
   * Login with credentials
   */
  async login(params: LoginDto): Promise<void> {
    const { userName, dbName, rememberMe } = params;

    // Call the domain use case
    await this.authUseCase.login(params);

    // Save credentials if rememberMe is true
    this.userCredentialsService.saveUserCredentials(userName, dbName, rememberMe);

    // Check if we're in a browser environment before accessing window
    if (typeof window === 'undefined') {
      return;
    }

    // TROC-317 / RWT-7864: ERP embed must land on UM Users, not dashboard (chrome-hide alone is not enough).
    window.location.href = readEmbeddedModeFromStorage() ? EUM_EMBED_USERS_PATH : DASHBOARD_PATH;
  }

  /**
   * Register with credentials
   */
  async register(params: RegisterDto): Promise<void> {
    return this.authUseCase.register(params);
  }

  /**
   * Logout user
   */
  logout(): void {
    this.authUseCase.logout();
  }

  /**
   * Reset password
   */
  async resetPassword(params: ResetPasswordDto): Promise<void> {
    return this.authUseCase.resetPassword(params);
  }

  /**
   * Verify token
   */
  verifyToken(token: string): boolean {
    return this.authUseCase.verifyToken(token);
  }

  /**
   * Get saved credentials
   */
  getSavedCredentials(): LoginValues {
    return this.userCredentialsService.getSavedCredentials();
  }
}
